import { RealTimeBooking } from "../../../types/TypeSession";

export const GLOBAL_CHAT_REAL_TIME_BOOKING_TTL_MS = 15 * 60 * 1000;
export const GLOBAL_CHAT_BOOKING_POLL_INTERVAL_MS = 5000;

export type BookingWorkflowState =
  | "pending"
  | "accepted"
  | "expired"
  | "missing";

export const getBookingWorkflowState = (
  booking: RealTimeBooking | undefined,
): BookingWorkflowState => {
  if (!booking) {
    return "missing";
  }

  if (booking.isAccepted) {
    return "accepted";
  }

  if (booking.isExpired) {
    return "expired";
  }

  return "pending";
};

export const getBookingCancelReason = (
  booking: RealTimeBooking | undefined,
  selectedLanguage?: string,
) => {
  if (!booking) {
    return "";
  }

  const localizedReason = booking.labels?.find(
    (label) => label.language === selectedLanguage,
  )?.text;

  return localizedReason || booking.cancelReason || "";
};

export const getBookingRemainingTime = (
  booking: RealTimeBooking | undefined,
  now = Date.now(),
  ttlMs = GLOBAL_CHAT_REAL_TIME_BOOKING_TTL_MS,
) => {
  if (!booking?.creationDate) {
    return { totalMs: 0, minutes: 0, seconds: 0 };
  }

  const totalMs = Math.max(
    0,
    new Date(booking.creationDate).getTime() + ttlMs - now,
  );

  return {
    totalMs,
    minutes: Math.floor((totalMs % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((totalMs % (1000 * 60)) / 1000),
  };
};

export const canJoinRealTimeBooking = (
  booking: RealTimeBooking | undefined,
) => {
  if (!booking?.bookedSession || !booking.creationDate) {
    return false;
  }

  const currentDate = new Date(
    new Date().toLocaleString("en-US", { timeZone: "Etc/GMT" }),
  );
  const sessionStartDate = new Date(
    String(booking.creationDate).replace("Z", ""),
  );
  const minutesElapsed = Math.floor(
    (currentDate.getTime() - sessionStartDate.getTime()) / (1000 * 60),
  );

  return minutesElapsed < Number(process.env.REACT_APP_SESSION_END_GAP);
};

export const shouldPollRealTimeBooking = (
  booking: RealTimeBooking | undefined,
) => getBookingWorkflowState(booking) === "pending";
